import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Upload, FileText, Languages, Download, Loader2, CheckCircle2, XCircle, Settings, AlertCircle, FileType, UserCircle, Coins, Clock, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = (() => {
  const url = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
  return url.endsWith('/api') ? url : `${url}/api`;
})();

const DocumentProcessor = () => {
  const { user, token, refreshUser } = useAuth();
  const [file, setFile] = useState(null);
  const [operation, setOperation] = useState('translate');
  const [sourceLang, setSourceLang] = useState('en');
  const [targetLang, setTargetLang] = useState('tr');
  const [outputFormat, setOutputFormat] = useState('txt');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [backendStatus, setBackendStatus] = useState(null);
  const [backendLoading, setBackendLoading] = useState(true);
  const [activeJob, setActiveJob] = useState(null);
  const [ocrConfirmation, setOcrConfirmation] = useState(null);
  const [sourceSearch, setSourceSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [resultFilename, setResultFilename] = useState(null);

  useEffect(() => {
    const checkBackendHealth = async () => {
      setBackendLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/health`);
        const data = await response.json();
        setBackendStatus(data);
      } catch (err) {
        setBackendStatus({ status: 'error', message: 'Backend bağlantısı kurulamadı' });
      } finally {
        setBackendLoading(false);
      }
    };

    const fetchLanguages = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/languages`);
        const data = await response.json();
        setLanguages(data);
      } catch (err) {
        setLanguages([
          { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
          { code: 'en', name: 'İngilizce', flag: '🇬🇧' },
          { code: 'de', name: 'Almanca', flag: '🇩🇪' },
          { code: 'fr', name: 'Fransızca', flag: '🇫🇷' },
          { code: 'es', name: 'İspanyolca', flag: '🇪🇸' },
        ]);
      }
    };

    const checkActiveJob = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${API_BASE_URL}/active-job`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.active) {
          setActiveJob(data);
          setProcessing(true);
          setProgress(`İşleminiz kuyrukta: ${data.filename}. Tahmini süre: ${data.estimated_minutes} dk. Tamamlanınca mail atacağız.`);
        }
      } catch (err) {
        console.error('Aktif iş kontrol hatası:', err);
      }
    };

    checkBackendHealth();
    fetchLanguages();
    checkActiveJob();
  }, [token]);

  // Alfabetik sıralanmış ve filtrelenmiş diller
  const sortedLanguages = [...languages].sort((a, b) => a.name.localeCompare(b.name, 'tr'));

  const filteredSourceLanguages = sortedLanguages.filter(lang =>
    lang.name.toLowerCase().includes(sourceSearch.toLowerCase()) ||
    lang.code.toLowerCase().includes(sourceSearch.toLowerCase())
  );

  const filteredTargetLanguages = sortedLanguages.filter(lang =>
    lang.name.toLowerCase().includes(targetSearch.toLowerCase()) ||
    lang.code.toLowerCase().includes(targetSearch.toLowerCase())
  );

  // Polling logic
  useEffect(() => {
    let interval;
    if (isPolling && jobId) {
      interval = setInterval(async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/status/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setJobStatus(data);
            if (data.status === 'completed') {
              setIsPolling(false);
              setDownloadUrl(`${API_BASE_URL}/download/${data.result.download_id}`);
              setResultFilename(data.result.filename);
              setProcessing(false);
              setOcrConfirmation(null);
            } else if (data.status === 'failed') {
              setIsPolling(false);
              setError(data.error || 'İşlem başarısız oldu.');
              setProcessing(false);
            }
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isPolling, jobId, token]);

  const handleDownload = async () => {
    if (!downloadUrl) return;
    try {
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = resultFilename || 'translated_file';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        alert('Dosya indirilemedi. Lütfen tekrar deneyin.');
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('İndirme sırasında bir hata oluştu.');
    }
  };

  const resetAll = () => {
    setFile(null);
    setJobId(null);
    setJobStatus(null);
    setIsPolling(false);
    setDownloadUrl(null);
    setResultFilename(null);
    setProcessing(false);
    setProgress('');
    setError(null);
    setOcrConfirmation(null);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    const validTypes = ['.pdf', '.txt', '.epub', '.docx'];
    const fileExt = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
    if (!validTypes.includes(fileExt)) {
      setError('Sadece PDF, TXT, EPUB ve DOCX dosyaları destekleniyor!');
      return;
    }
    if (selectedFile.size > 50 * 1024 * 1024) {
      setError('Dosya boyutu 50MB\'dan küçük olmalıdır!');
      return;
    }
    setFile(selectedFile);
    setError(null);
    setOcrConfirmation(null);

    const format = fileExt.substring(1);
    setOutputFormat(format);
  };

  const processDocument = async (confirmOcr = false) => {
    if (!file) {
      setError('Lütfen bir dosya seçin!');
      return;
    }
    setProcessing(true);
    setError(null);
    setProgress('🚀 İşlem başlatılıyor...');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('operation', operation);
    formData.append('source_lang', sourceLang);
    formData.append('target_lang', targetLang);
    formData.append('output_format', outputFormat);
    formData.append('confirm_ocr', confirmOcr);

    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_BASE_URL}/process`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        if (data.requires_ocr_confirmation) {
          setOcrConfirmation({
            charCount: data.char_count,
            estimatedCost: data.estimated_cost
          });
          setProcessing(false);
          setProgress('');
          return;
        }
        throw new Error(data.error || 'İşlem başarısız oldu');
      }

      if (data.success && data.job_id) {
        setJobId(data.job_id);
        setIsPolling(true);
        setJobStatus({ status: 'pending', message: data.message });
        setOcrConfirmation(null);
      } else {
        throw new Error('İşlem başlatılamadı');
      }
    } catch (err) {
      setError(err.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
      setProgress('');
      setProcessing(false);
      if (refreshUser) refreshUser();
    }
  };

  return (
    <div className="min-h-screen bg-soft-blue p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <img src="/logo192.png" alt="CeviriPro Logo" className="w-12 h-12 object-contain drop-shadow-sm" />
              <div>
                <h1 className="text-3xl font-bold text-gradient-blue">
                  Doküman İşleyici
                </h1>
                <p className="text-slate-500 text-sm">PDF, TXT, EPUB ve DOCX yükleyin, çevirin</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {user && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 backdrop-blur-sm border border-blue-100 rounded-full">
                  <Coins className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-700">{(user.tokens_remaining ?? 0).toLocaleString('tr-TR')} Token</span>
                </div>
              )}
              <Link
                to="/profile"
                className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm border border-blue-100 rounded-xl shadow-sm hover:shadow-md hover:border-blue-200 transition-all text-slate-700 text-sm font-medium"
              >
                <UserCircle className="w-4 h-4" />
                Profilim
              </Link>
            </div>
          </div>
        </div>

        {backendLoading && (
          <div className="bg-white/80 backdrop-blur-sm fixed inset-0 z-50 flex items-center justify-center p-4 text-center">
            <div>
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-900 mb-1">CeviriPro Yükleniyor</h3>
              <p className="text-slate-500 text-sm">Backend sunucusuna bağlanılıyor...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-8 bg-red-50/80 backdrop-blur-sm border border-red-100 rounded-2xl p-4 flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
              <button onClick={() => setError(null)} className="text-red-600 text-xs font-bold mt-1 uppercase tracking-wider hover:text-red-700">
                Kapat
              </button>
            </div>
          </div>
        )}

        {isPolling ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 text-center border border-white/50">
            <div className="relative inline-block mb-8">
              <div className="w-24 h-24 border-4 border-blue-100 border-t-blue-500 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Clock className="w-10 h-10 text-blue-500" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">İşleminiz Devam Ediyor</h2>
            <div className="max-w-md mx-auto bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100 mb-8">
              <p className="text-blue-800 font-medium mb-2">{jobStatus?.message || progress}</p>
              {jobStatus?.status === 'processing' && jobStatus?.total_chunks > 0 && (
                <div className="mt-4 w-full bg-blue-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-blue-500 h-full transition-all duration-500"
                    style={{ width: `${(jobStatus.current_chunk / jobStatus.total_chunks) * 100}%` }}
                  ></div>
                </div>
              )}
              <div className="flex items-center justify-center gap-2 text-blue-600 text-sm mt-4">
                <Mail className="w-4 h-4" />
                <span>Tamamlandığında email ile bildirilecektir</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/profile" className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-2xl hover:shadow-xl hover:shadow-blue-500/30 transition-all font-semibold">
                <UserCircle className="w-5 h-5" />
                Profilimden Takip Et
              </Link>
            </div>
          </div>
        ) : downloadUrl ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 text-center border border-white/50 animate-in fade-in zoom-in duration-500">
            <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
              <Download className="w-12 h-12 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Dosyanız Hazır!</h2>
            <p className="text-slate-500 mb-8 font-medium">İşlem başarıyla tamamlandı. Dosyanızı hemen indirebilirsiniz.</p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <button
                onClick={handleDownload}
                className="flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-2xl hover:shadow-2xl hover:shadow-blue-500/40 transition-all font-bold text-lg"
              >
                <Download className="w-6 h-6" />
                Hemen İndir
              </button>
              <button
                onClick={resetAll}
                className="px-8 py-4 bg-slate-100 text-slate-600 rounded-2xl hover:bg-slate-200 transition-all font-semibold"
              >
                Yeni İşlem
              </button>
            </div>
          </div>
        ) : processing ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 text-center border border-white/50">
            <div className="relative inline-block mb-8">
              <div className="w-24 h-24 border-4 border-blue-100 border-t-blue-500 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-blue-500" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{progress}</h2>
          </div>
        ) : null}

        {!isPolling && !downloadUrl && !processing && (
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-6 md:p-8 border border-white/50">
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                <Settings className="w-4 h-4 inline mr-2" />
                İşlem Türü
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setOperation('extract')}
                  className={`p-4 rounded-2xl border-2 transition-all ${operation === 'extract' ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-blue-100 hover:border-blue-200'}`}
                >
                  <FileText className={`w-6 h-6 mx-auto mb-2 ${operation === 'extract' ? 'text-blue-600' : 'text-slate-400'}`} />
                  <div className="font-semibold text-slate-800">Metin Çıkar</div>
                  <div className="text-xs text-slate-500 mt-1">OCR Destekli</div>
                </button>
                <button
                  onClick={() => setOperation('translate')}
                  className={`p-4 rounded-2xl border-2 transition-all ${operation === 'translate' ? 'border-indigo-500 bg-indigo-50 shadow-md' : 'border-blue-100 hover:border-blue-200'}`}
                >
                  <Languages className={`w-6 h-6 mx-auto mb-2 ${operation === 'translate' ? 'text-indigo-600' : 'text-slate-400'}`} />
                  <div className="font-semibold text-slate-800">Çeviri Yap</div>
                </button>
              </div>
            </div>

            {operation === 'translate' && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Kaynak Dil</label>
                    <div className="relative group">
                      <input
                        type="text"
                        placeholder="Dil ara..."
                        value={sourceSearch}
                        onChange={(e) => setSourceSearch(e.target.value)}
                        className="w-full mb-2 p-2 text-sm border border-blue-100 rounded-lg focus:border-blue-400 focus:outline-none bg-white/70"
                      />
                      <select
                        value={sourceLang}
                        onChange={(e) => setSourceLang(e.target.value)}
                        className="w-full p-3 border-2 border-blue-100 rounded-xl focus:border-blue-400 focus:outline-none transition-all bg-white/50"
                      >
                        {filteredSourceLanguages.map(lang => (
                          <option key={lang.code} value={lang.code}>{lang.flag} {lang.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Hedef Dil</label>
                    <div className="relative group">
                      <input
                        type="text"
                        placeholder="Dil ara..."
                        value={targetSearch}
                        onChange={(e) => setTargetSearch(e.target.value)}
                        className="w-full mb-2 p-2 text-sm border border-blue-100 rounded-lg focus:border-blue-400 focus:outline-none bg-white/70"
                      />
                      <select
                        value={targetLang}
                        onChange={(e) => setTargetLang(e.target.value)}
                        className="w-full p-3 border-2 border-blue-100 rounded-xl focus:border-blue-400 focus:outline-none transition-all bg-white/50"
                      >
                        {filteredTargetLanguages.map(lang => (
                          <option key={lang.code} value={lang.code}>{lang.flag} {lang.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                <FileType className="w-4 h-4 inline mr-2" />
                Çıktı Formatı
              </label>
              <div className="grid grid-cols-4 gap-2">
                {['txt', 'pdf', 'docx', 'epub'].map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => setOutputFormat(fmt)}
                    className={`p-3 rounded-xl border-2 transition-all font-medium uppercase ${outputFormat === fmt ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm' : 'border-blue-100 text-slate-600 hover:border-blue-200'}`}
                  >
                    {fmt}
                  </button>
                ))}
              </div>
            </div>

            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-3 border-dashed rounded-2xl p-8 text-center transition-all ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-blue-200 hover:border-blue-300'}`}
            >
              {!file ? (
                <div>
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-10 h-10 text-blue-500" />
                  </div>
                  <p className="text-lg font-semibold text-slate-700 mb-2">Dosyayı buraya sürükleyin veya seçin</p>
                  <p className="text-sm text-slate-500 mb-4">PDF, TXT, EPUB, DOCX (Max 50MB)</p>
                  <input
                    type="file"
                    accept=".pdf,.txt,.epub,.docx"
                    onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl cursor-pointer hover:shadow-xl hover:shadow-blue-500/30 transition-all">
                    Dosya Seç
                  </label>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-8 h-8 text-blue-600" />
                    <div className="text-left">
                      <p className="font-semibold text-slate-800">{file.name}</p>
                      <p className="text-sm text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                  <button onClick={() => setFile(null)} className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl transition-all">Değiştir</button>
                </div>
              )}
            </div>

            <button
              onClick={() => processDocument(false)}
              disabled={!file || processing || !backendStatus || backendStatus.status !== 'ok'}
              className={`w-full mt-6 py-4 rounded-xl font-semibold text-white text-lg transition-all ${!file || processing || !backendStatus || backendStatus.status !== 'ok' ? 'bg-slate-300 cursor-not-allowed' : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:shadow-xl hover:shadow-blue-500/30'}`}
            >
              🌐 Çeviriyi Başlat
            </button>
          </div>
        )}

        {/* OCR Onay Modalı */}
        {ocrConfirmation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-center text-slate-900 mb-2">OCR Gerekli</h3>
              <p className="text-center text-slate-600 mb-6 text-sm">
                Yüklediğiniz PDF dosyası taranmış görsellerden oluştuğu için metin çıkarımı için OCR (Optik Karakter Tanıma) kullanılması gerekiyor.
              </p>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-500">Tespit Edilen Karakter:</span>
                  <span className="text-sm font-bold text-slate-700">{ocrConfirmation.charCount.toLocaleString('tr-TR')}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-500">OCR Çarpanı:</span>
                  <span className="text-sm font-bold text-amber-600">x 1.3</span>
                </div>
                <div className="h-px bg-blue-200 my-3"></div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-slate-700">Toplam Token Maliyeti:</span>
                  <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-100 text-blue-700 rounded-lg">
                    <Coins className="w-4 h-4" />
                    <span className="font-bold">{ocrConfirmation.estimatedCost.toLocaleString('tr-TR')}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setOcrConfirmation(null)}
                  className="flex-1 py-3 px-4 rounded-xl font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
                >
                  İptal Et
                </button>
                <button
                  onClick={() => processDocument(true)}
                  className="flex-1 py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-lg hover:shadow-amber-200 transition-all"
                >
                  Onayla ve Çevir
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card-soft p-4 rounded-xl">
            <div className="text-3xl mb-2">🔍</div>
            <h3 className="font-bold text-slate-800 mb-1">OCR Desteği</h3>
            <p className="text-sm text-slate-600">Taranmış PDF'lerden metin çıkarın</p>
          </div>
          <div className="card-soft p-4 rounded-xl">
            <div className="text-3xl mb-2">🌍</div>
            <h3 className="font-bold text-slate-800 mb-1">Hızlı Çeviri</h3>
            <p className="text-sm text-slate-600">Yapay zeka motoru ile kaliteli sonuçlar</p>
          </div>
          <div className="card-soft p-4 rounded-xl">
            <div className="text-3xl mb-2">⚡</div>
            <h3 className="font-bold text-slate-800 mb-1">Çoklu Format</h3>
            <p className="text-sm text-slate-600">PDF, DOCX, EPUB ve TXT çıktı desteği</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentProcessor;